import { CaseReportExternalProps } from 'app/area/case-report/case-report-container';
import { FavouriteScreen } from 'app/area/favourite/components/favourite-screen';
import { searchTranslation } from 'app/area/favourite/constants';
import { useInstanceOptions } from 'app/area/favourite/favourite-context';
import { 
  useSavedReportState,
  useFavouriteDefinitions, 
  useActionCreators, 
  useSortOptions,
  useReport,
  useSearchQuery,
  useLoadFavouriteDefinitionEffect,
  useSearchRequiredUsers,
  useUrlMultipleSuppliers,
  useReplaceDomainNodes,
  useExecuteStatus
} from 'app/area/favourite/hooks';
import { EXECUTE_STATUS } from 'app/area/reports/containers/chart/types-store';
import { StatisticalReportExternalProps } from 'app/area/reports/types';
import { STATE } from 'app/constants';
import { useSharedState } from 'app/infrastructure/redux/hooks';
import { useTranslation } from 'app/infrastructure/translate/hooks';
import * as React from 'react';

interface FavouriteProps {
  embeddedFavourite?: boolean;
  editSavedFavourite?: boolean;
  initiallyOpenFilter?: boolean;
  favouriteId: number;
  overrideReport?: number;
  caseReportExternalProps?: CaseReportExternalProps;
  statisticalReportExternalProps?: StatisticalReportExternalProps;
  windowAsScrollableAncestor?: boolean;
}

export function Favourite({
  embeddedFavourite,
  editSavedFavourite,
  initiallyOpenFilter,
  favouriteId,
  overrideReport,
  caseReportExternalProps,
  statisticalReportExternalProps,
  windowAsScrollableAncestor
}: FavouriteProps) {

  const {
    hideHeader,
    hideFilters,
    hideFilterReplacements
  } = useInstanceOptions();

  const {
    definition,
    workingDefinition,
    updateDefinition,
    updateReplaceDomainNodes,
    loadExtendedReplaceDomainNodes,
    fetchingDefinition
  } = useFavouriteDefinitions();

  const executeStatus = useExecuteStatus();

  const requestPrint = location.search.includes('print=true');

  const canPrint = !embeddedFavourite && executeStatus === EXECUTE_STATUS.SUCCEEDED && requestPrint;

  useLoadFavouriteDefinitionEffect(favouriteId, embeddedFavourite, canPrint);

  const { fetchingUpdateReplaceDomainNodes, fetchingExtendedReplaceDomainNodes } = useReplaceDomainNodes();

  const {
    searchQueryTemp,
    onChange,
    onSearch
  } = useSearchQuery();

  const {
    createReport,
    updateExistingReport,
    onCloseReport,
    onCreateReport,
    onUpdateReport
  } = useSavedReportState(editSavedFavourite);
  
  const {
    report,
    fetchingReportCategory,
    reportCategory
  } = useReport(overrideReport, !hideHeader);
  
  const {
    fetchingSortOptions,
    loadSortOptions,
    updateSortOptions,
    sortOptions,
    sortOrderFields
  } = useSortOptions(report);
  
  const {
    displayExportToZip,
    downloadReport,
    getMulitpleSuppliersUrl,
    getSearchRequiredUsers
  } = useActionCreators();

  const searchRequiredUsers = useSearchRequiredUsers();

  const urlMultipleSuppliers = useUrlMultipleSuppliers();
  
  const translation = useTranslation(STATE.favourite, searchTranslation);
  
  const sharedState = useSharedState();
  
  const showUpdate = definition?.validState?.owners?.some(owner => owner.v === sharedState?.id);

  return <FavouriteScreen
    hideHeader={hideHeader}
    hideFilters={hideFilters}
    hideFilterReplacements={hideFilterReplacements}
    translation={translation}
    definition={definition}
    workingDefinition={workingDefinition}
    initiallyOpenFilter={initiallyOpenFilter}
    isCreateReportOpen={createReport}
    updateExistingReport={updateExistingReport}
    onCreateReport={onCreateReport}
    onUpdateReport={onUpdateReport}
    onCloseReport={onCloseReport}
    fetchingReportCategory={fetchingReportCategory}
    fetchingDefinition={fetchingDefinition}
    fetchingSortOptions={fetchingSortOptions}
    loadSortOptions={loadSortOptions}
    displayExportToZip={displayExportToZip}
    downloadReport={downloadReport}
    getMultipleSuppliersUrl={getMulitpleSuppliersUrl}
    urlMultipleSuppliers={urlMultipleSuppliers}
    getSearchRequiredUsers={getSearchRequiredUsers}
    searchQueryTemp={searchQueryTemp}
    onChange={onChange}
    onSearch={onSearch}
    report={report}
    reportCategory={reportCategory}
    updateDefinition={updateDefinition}
    updateReplaceDomainNodes={updateReplaceDomainNodes}
    fetchingUpdateReplaceDomainNodes={fetchingUpdateReplaceDomainNodes}
    fetchingExtendedReplaceDomainNodes={fetchingExtendedReplaceDomainNodes}
    loadExtendedReplaceDomainNodes={loadExtendedReplaceDomainNodes}
    updateSortOptions={updateSortOptions}
    sharedState={sharedState}
    sortOptions={sortOptions}
    caseReportExternalProps={caseReportExternalProps}
    statisticalReportExternalProps={statisticalReportExternalProps}
    favouriteId={favouriteId}
    showUpdate={showUpdate}
    searchRequiredUsers={searchRequiredUsers}
    sortOrderFields={sortOrderFields}
    windowAsScrollableAncestor={windowAsScrollableAncestor}
  />;
}
